import React from 'react';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

import type { IAvatarProps, IUserInfo } from './Avatar.types';

export const Avatar: React.FC<IAvatarProps> = ({
  user,
  size = 'md',
  variant = 'brand',
  showName = false,
  className = '',
  title,
}) => {
  
  const getInitials = (user: IUserInfo) => {
      return user?.firstName && user?.lastName 
      ? `${user.firstName[0].toLocaleUpperCase()}${user.lastName[0].toLocaleUpperCase()}` 
      : user?.username?.slice(0, 2).toLocaleUpperCase();
  };

  const getDisplayName = (user: IUserInfo) => {
    return user?.firstName && user?.lastName 
    ? `${user?.firstName} ${user?.lastName}`
    : user?.username;
  };

  // Size mapping for avatar circle
  const sizeMapper = {
    xs: 'w-bg-v2-2xl h-bg-v2-2xl', // 20px
    sm: 'w-bg-v2-4xl h-bg-v2-4xl', // 28px
    md: 'w-bg-v2-4xl h-bg-v2-4xl', // 32px
    lg: 'w-bg-v2-5xl h-bg-v2-5xl', // 40px
    xl: 'w-bg-v2-6xl h-bg-v2-6xl', // 48px
  };

  // Text size mapping based on avatar size
  const textSizeMapper = {
    xs: 'text-xs' as const,
    sm: 'text-xs' as const,
    md: 'text-sm' as const,
    lg: 'text-md' as const,
    xl: 'text-md' as const,
  };

  // Variant color mapping
  const variantMapper = {
    primary: 'bg-bg-primary-solid',
    secondary: 'bg-bg-secondary-solid',
    brand: 'bg-bg-brand-solid',
  };


  const avatarElement = showName ? (
    <div className={`flex items-center gap-bg-v2-md ${className}`}>
      <div className={`flex items-center p-bg-v2-md justify-center rounded-bg-v2-full ${variantMapper[variant]} ${sizeMapper[size]} shrink-0 overflow-hidden cursor-default`}>
        <Text variant={textSizeMapper[size]} weight="semibold" color="white" className="font-bg-v2-sans">
          {getInitials(user)}
        </Text>
      </div>
      <Text 
        variant="text-sm" 
        weight="semibold"
        color="primary"
        className="font-bg-v2-sans whitespace-nowrap"
      >
        {getDisplayName(user)}
      </Text>
    </div>
  ) : (
    <div className={`flex items-center p-bg-v2-md justify-center rounded-bg-v2-full ${variantMapper[variant]} ${sizeMapper[size]} shrink-0 overflow-hidden cursor-default ${className}`}>
      <Text variant={textSizeMapper[size]} weight="semibold" color="white" className="font-bg-v2-sans">
        {getInitials(user)}
      </Text>
    </div>
  );

  // Wrap with Tooltip if title is provided
  if (title) {
    return (
      <Tooltip content={title} delay={200} position="top">
        {avatarElement}
      </Tooltip>
    );
  }

  return avatarElement;
};

